// AICA Alert Component - Meridian Dark Design System
// In-page notifications and warnings
// Bloomberg Terminal style: Clear status communication with variant-based colors

import * as React from 'react';
import { AnimatePresence, m } from 'framer-motion';
import { cn } from '@/lib/utils';
import { buildTransition } from '@/components/transitions';
import {
  Info,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  X,
  type LucideIcon,
} from 'lucide-react';

/**
 * Alert component variants with Meridian Dark color scheme
 */
export type AlertVariant = 'info' | 'success' | 'warning' | 'danger';

/**
 * Get icon component for alert variant
 */
const getVariantIcon = (variant: AlertVariant): LucideIcon => {
  const icons: Record<AlertVariant, LucideIcon> = {
    info: Info,
    success: CheckCircle2,
    warning: AlertTriangle,
    danger: XCircle,
  };
  return icons[variant];
};

/**
 * Get variant styles following Meridian Dark specifications
 */
const getVariantStyles = (variant: AlertVariant): string => {
  const styles: Record<AlertVariant, string> = {
    info: 'bg-info-dim border-border text-info-fg',
    success: 'bg-success-dim border-border text-success-fg',
    warning: 'bg-warning-dim border-border text-warning-fg',
    danger: 'bg-danger-dim border-danger text-danger-fg',
  };
  return styles[variant];
};

/**
 * Alert Context for compound components
 */
interface AlertContextValue {
  variant: AlertVariant;
  dismissible?: boolean;
  onDismiss?: () => void;
}

const AlertContext = React.createContext<AlertContextValue | undefined>(undefined);

const useAlertContext = () => {
  const context = React.useContext(AlertContext);
  if (!context) {
    throw new Error('alert_context_required');
  }
  return context;
};

/**
 * Alert Component - Root container
 * Meridian Dark Design System compliant
 *
 * Features:
 * - 4 variants: info, success, warning, danger
 * - Optional dismissible with close button
 * - Composable with AlertIcon, AlertTitle, AlertDescription, AlertClose
 * - WCAG 2.1 AA compliant
 * - Semantic HTML with proper ARIA attributes
 *
 * @example
 * ```tsx
 * // Simple usage
 * <Alert variant="success">
 *   Your changes have been saved.
 * </Alert>
 *
 * // With title and description
 * <Alert variant="info">
 *   <AlertIcon />
 *   <AlertTitle>Heads up!</AlertTitle>
 *   <AlertDescription>
 *     You can add components to your app using the cli.
 *   </AlertDescription>
 *   <AlertClose />
 * </Alert>
 * ```
 */
export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Alert variant determines color scheme
   */
  variant?: AlertVariant;
  /**
   * Whether alert can be dismissed
   */
  dismissible?: boolean;
  /**
   * Callback when alert is dismissed
   */
  onDismiss?: () => void;
  /**
   * Additional className
   */
  className?: string;
  /**
   * Children elements (text or compound components)
   */
  children: React.ReactNode;
}

export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  (
    { variant = 'info', dismissible = false, onDismiss, className, children, ...props },
    ref
  ) => {
    const [isVisible, setIsVisible] = React.useState(true);

    const handleDismiss = React.useCallback(() => {
      setIsVisible(false);
      onDismiss?.();
    }, [onDismiss]);

    // Determine aria-live based on variant
    const ariaLive =
      variant === 'danger' || variant === 'warning' ? 'assertive' : 'polite';

    // Check if children are compound components or plain text
    const hasCompoundComponents = React.Children.toArray(children).some(
      (child) =>
        React.isValidElement(child) &&
        (child.type === AlertIcon ||
          child.type === AlertTitle ||
          child.type === AlertDescription ||
          child.type === AlertClose)
    );

    return (
      <AnimatePresence>
        {isVisible && (
          <AlertContext.Provider
            value={{ variant, dismissible, onDismiss: handleDismiss }}
          >
            <m.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={buildTransition('fast', 'smooth')}
            >
              <div
                ref={ref}
                role="alert"
                aria-live={ariaLive}
                className={cn(
                  // Base styles
                  'relative rounded-card border px-4 py-3 text-sm overflow-hidden',
                  // Variant styles
                  getVariantStyles(variant),
                  // Ensure proper text color inheritance for simple text content
                  '[&>svg]:absolute [&>svg]:left-4 [&>svg]:top-3.5',
                  '[&>svg~*]:pl-7',
                  className
                )}
                {...props}
              >
                {/* If plain text, add default icon */}
                {!hasCompoundComponents && (
                  <>
                    <AlertIcon />
                    <div className="pl-7">{children}</div>
                  </>
                )}
                {/* If compound components, render as-is */}
                {hasCompoundComponents && children}
              </div>
            </m.div>
          </AlertContext.Provider>
        )}
      </AnimatePresence>
    );
  }
);

Alert.displayName = 'Alert';

/**
 * AlertIcon - Icon component that auto-selects based on variant
 *
 * @example
 * ```tsx
 * <Alert variant="success">
 *   <AlertIcon />
 *   Success message
 * </Alert>
 * ```
 */
export interface AlertIconProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Override the default icon
   */
  icon?: LucideIcon;
  /**
   * Additional className
   */
  className?: string;
}

export const AlertIcon = React.forwardRef<HTMLDivElement, AlertIconProps>(
  ({ icon, className, ...props }, ref) => {
    const { variant } = useAlertContext();
    const Icon = icon || getVariantIcon(variant);

    return (
      <div ref={ref} className={cn('absolute left-4 top-3.5', className)} {...props}>
        <Icon className="h-4 w-4" aria-hidden="true" />
      </div>
    );
  }
);

AlertIcon.displayName = 'AlertIcon';

/**
 * AlertTitle - Title text for the alert
 *
 * @example
 * ```tsx
 * <Alert variant="warning">
 *   <AlertIcon />
 *   <AlertTitle>Warning</AlertTitle>
 *   <AlertDescription>Please review before continuing.</AlertDescription>
 * </Alert>
 * ```
 */
export interface AlertTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /**
   * Additional className
   */
  className?: string;
  /**
   * Children (title text)
   */
  children: React.ReactNode;
}

export const AlertTitle = React.forwardRef<HTMLHeadingElement, AlertTitleProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <h5
        ref={ref}
        className={cn(
          'text-sm font-medium text-fg mb-1 leading-none tracking-tight',
          className
        )}
        {...props}
      >
        {children}
      </h5>
    );
  }
);

AlertTitle.displayName = 'AlertTitle';

/**
 * AlertDescription - Description text for the alert
 *
 * @example
 * ```tsx
 * <Alert variant="info">
 *   <AlertIcon />
 *   <AlertTitle>Note</AlertTitle>
 *   <AlertDescription>
 *     This feature is currently in beta.
 *   </AlertDescription>
 * </Alert>
 * ```
 */
export interface AlertDescriptionProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Additional className
   */
  className?: string;
  /**
   * Children (description text)
   */
  children: React.ReactNode;
}

export const AlertDescription = React.forwardRef<HTMLDivElement, AlertDescriptionProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn('text-sm text-fg-muted leading-relaxed', className)}
        {...props}
      >
        {children}
      </div>
    );
  }
);

AlertDescription.displayName = 'AlertDescription';

/**
 * AlertClose - Close button for dismissible alerts
 *
 * @example
 * ```tsx
 * <Alert variant="info" dismissible>
 *   <AlertIcon />
 *   Information message
 *   <AlertClose />
 * </Alert>
 * ```
 */
export interface AlertCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Additional className
   */
  className?: string;
}

export const AlertClose = React.forwardRef<HTMLButtonElement, AlertCloseProps>(
  ({ className, ...props }, ref) => {
    const { onDismiss } = useAlertContext();

    return (
      <button
        ref={ref}
        type="button"
        onClick={onDismiss}
        className={cn(
          // Positioning
          'absolute right-2 top-2',
          // Styling
          'p-1 rounded-sm',
          // Colors
          'text-fg-subtle hover:text-fg',
          // Background on hover
          'hover:bg-bg-subtle',
          // Transitions
          'transition-all duration-150',
          // Focus styles
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-bg-base',
          className
        )}
        aria-label="Close"
        {...props}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    );
  }
);

AlertClose.displayName = 'AlertClose';

/**
 * Pre-composed dismissible alert for convenience
 *
 * @example
 * ```tsx
 * <DismissibleAlert variant="warning">
 *   This action cannot be undone.
 * </DismissibleAlert>
 * ```
 */
export interface DismissibleAlertProps extends Omit<AlertProps, 'dismissible'> {
  /**
   * Alert variant
   */
  variant?: AlertVariant;
  /**
   * Whether to show icon
   */
  showIcon?: boolean;
  /**
   * Alert content
   */
  children: React.ReactNode;
}

export function DismissibleAlert({
  variant = 'info',
  showIcon = true,
  children,
  ...props
}: DismissibleAlertProps) {
  return (
    <Alert variant={variant} dismissible {...props}>
      {showIcon && <AlertIcon />}
      <div className={showIcon ? 'pl-7 pr-8' : 'pr-8'}>{children}</div>
      <AlertClose />
    </Alert>
  );
}

/**
 * Alert with title and description for common use case
 *
 * @example
 * ```tsx
 * <AlertWithTitle
 *   variant="success"
 *   title="Success!"
 *   description="Your profile has been updated."
 * />
 * ```
 */
export interface AlertWithTitleProps extends Omit<AlertProps, 'children'> {
  /**
   * Alert title
   */
  title: string;
  /**
   * Alert description
   */
  description?: string;
  /**
   * Whether to show icon
   */
  showIcon?: boolean;
  /**
   * Whether alert can be dismissed
   */
  dismissible?: boolean;
}

export function AlertWithTitle({
  variant = 'info',
  title,
  description,
  showIcon = true,
  dismissible = false,
  ...props
}: AlertWithTitleProps) {
  return (
    <Alert variant={variant} dismissible={dismissible} {...props}>
      {showIcon && <AlertIcon />}
      <div className={showIcon ? 'pl-7' : ''}>
        <AlertTitle>{title}</AlertTitle>
        {description && <AlertDescription>{description}</AlertDescription>}
      </div>
      {dismissible && <AlertClose />}
    </Alert>
  );
}

// Export everything
export default Alert;
